import React from "react";
import { safeJSONParse, snakeToTitleCase } from "../../../utils/helper";
import styles from "../agent-response.module.scss";

type SyntheticResumeAgentProps = {
  parsedContent: any;
  agentId?: string;
};

export default function SyntheticResumeAgent({
  parsedContent,
  agentId,
}: SyntheticResumeAgentProps): JSX.Element {
  // Parse the answer field first
  const answer =
    typeof parsedContent?.answer === "string"
      ? safeJSONParse(parsedContent.answer)
      : parsedContent?.answer || {};

  // Check if data is inside an 'output' field (HR workflow format)
  let outputData = answer;
  if (typeof parsedContent?.output === "string") {
    outputData = safeJSONParse(parsedContent.output);
  } else if (
    parsedContent?.output &&
    typeof parsedContent.output === "object"
  ) {
    outputData = parsedContent.output;
  }

  // Parse skill_inference_agent or jd_composer_agent data from answer or output
  let skillInferenceData = {};
  let isSkillInferenceWorkflow = false; // Detect if this is from Skill Inference Agent workflow

  if (typeof answer?.skill_inference_agent === "string") {
    skillInferenceData = safeJSONParse(answer.skill_inference_agent);
    isSkillInferenceWorkflow = true;
  } else if (typeof outputData?.skill_inference_agent === "string") {
    skillInferenceData = safeJSONParse(outputData.skill_inference_agent);
    isSkillInferenceWorkflow = true;
  } else if (answer?.skill_inference_agent) {
    skillInferenceData = answer.skill_inference_agent;
    isSkillInferenceWorkflow = true;
  } else if (outputData?.skill_inference_agent) {
    skillInferenceData = outputData.skill_inference_agent;
    isSkillInferenceWorkflow = true;
  } else if (typeof answer?.jd_composer_agent === "string") {
    skillInferenceData = safeJSONParse(answer.jd_composer_agent);
  } else if (typeof outputData?.jd_composer_agent === "string") {
    skillInferenceData = safeJSONParse(outputData.jd_composer_agent);
  } else if (answer?.jd_composer_agent) {
    skillInferenceData = answer.jd_composer_agent;
  } else if (outputData?.jd_composer_agent) {
    skillInferenceData = outputData.jd_composer_agent;
  }

  // Parse jd_details_agent data (contains overview) from answer or output
  let jdDetailsData = {};
  if (typeof answer?.jd_details_agent === "string") {
    jdDetailsData = safeJSONParse(answer.jd_details_agent);
  } else if (typeof outputData?.jd_details_agent === "string") {
    jdDetailsData = safeJSONParse(outputData.jd_details_agent);
  } else if (answer?.jd_details_agent) {
    jdDetailsData = answer.jd_details_agent;
  } else if (outputData?.jd_details_agent) {
    jdDetailsData = outputData.jd_details_agent;
  }

  // Get overview and responsibilities from jd_details_agent
  const overview = jdDetailsData?.overview || answer?.overview;
  const responsibilities =
    jdDetailsData?.responsibilities || answer?.responsibilities;

  // Get prompt/question from the response
  const question = parsedContent?.question || parsedContent?.prompt;

  // Merge skills data - use skillInferenceData if available, otherwise fallback to answer (old format)
  const mergedData = {
    ...(Object.keys(skillInferenceData).length > 0
      ? skillInferenceData
      : answer),
    overview: overview,
    responsibilities: responsibilities,
  };

  return (
    <div
      style={{
        lineHeight: "1.6",
        color: "#333",
      }}
    >
      {/* Title + Job Type */}
      {(mergedData.title || mergedData.job_type) && (
        <h2 style={{ marginBottom: "7px" }}>
          Based on recent skill inference analysis, below are the core skills
          and preferred qualifications for{" "}
          {mergedData.job_type ? ` ${mergedData.job_type}` : ""}
        </h2>
      )}

      {/* Overview - shown after user confirms synthetic profile */}
      {mergedData.overview && (
        <div style={{ marginBottom: "20px" }}>
          <h3 style={{ color: "#08ADC2", marginBottom: "10px" }}>Overview</h3>
          <div
            style={{
              backgroundColor: "#f8f9fa",
              padding: "15px",
              borderRadius: "8px",
              // borderLeft: "4px solid #08ADC2",
              lineHeight: "1.8",
              fontSize: "14px",
            }}
          >
            {mergedData.overview}
          </div>
        </div>
      )}

      {/* Responsibilities */}
      {Array.isArray(mergedData.responsibilities) &&
        mergedData.responsibilities.length > 0 && (
          <div style={{ marginBottom: "20px" }}>
            <h3 style={{ color: "#08ADC2", marginBottom: "10px" }}>
              Responsibilities
            </h3>
            <ul style={{ margin: "5px 0", lineHeight: "1.8" }}>
              {mergedData.responsibilities.map(
                (responsibility: string, idx: number) => (
                  <li key={idx}>{responsibility}</li>
                ),
              )}
            </ul>
          </div>
        )}

      {/* Functional Skills */}
      <div>
        <h3 style={{ color: "#08ADC2" }}>Functional / Technical Skills</h3>

        {Array.isArray(mergedData.functional_skills) && (
          <div style={{ marginBottom: "7px", marginLeft: "20px" }}>
            <h3>Core Technical Skills</h3>
            <ul style={{ margin: "5px 0" }}>
              {mergedData.functional_skills.map(
                (skill: string, idx: number) => (
                  <li key={idx}>{skill}</li>
                ),
              )}
            </ul>
          </div>
        )}

        {Array.isArray(mergedData.future_skills) && (
          <div style={{ marginBottom: "7px", marginLeft: "20px" }}>
            <h3>Future Ready Skills</h3>

            {!mergedData.future_skills.length && (
              <div style={{ fontSize: "14px", marginLeft: "24px" }}>
                No Future Ready Skills as of now
              </div>
            )}

            <ul style={{ margin: "5px 0" }}>
              {mergedData.future_skills.map((skill: string, idx: number) => (
                <li key={idx}>{skill}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Required Qualifications - Hidden only for Skill Inference Agent */}
      {!isSkillInferenceWorkflow &&
        agentId !== "skill_inference_agent" &&
        agentId !== "modify_requirements" &&
        mergedData.required_qualifications && (
          <div style={{ marginBottom: "7px" }}>
            <h3>Required Qualifications</h3>
            <ul style={{ margin: "5px 0" }}>
              {mergedData.required_qualifications.education && (
                <li>
                  Education: {mergedData.required_qualifications.education}
                </li>
              )}
              {mergedData.required_qualifications.experience && (
                <li>
                  Experience: {mergedData.required_qualifications.experience}
                </li>
              )}
              {mergedData.required_qualifications.location && (
                <li>Location: {mergedData.required_qualifications.location}</li>
              )}
              {mergedData.required_qualifications.previous_company && (
                <li>
                  Previous Company:{" "}
                  {mergedData.required_qualifications.previous_company}
                </li>
              )}
            </ul>
          </div>
        )}

      {/* Preferred Qualifications - Hidden only for Skill Inference Agent */}
      {!isSkillInferenceWorkflow &&
        agentId !== "skill_inference_agent" &&
        agentId !== "modify_requirements" &&
        Array.isArray(mergedData.preferred_qualifications) &&
        mergedData.preferred_qualifications.length > 0 && (
          <div style={{ marginBottom: "7px" }}>
            <h3>Preferred Qualifications</h3>
            <ul style={{ margin: "5px 0" }}>
              {mergedData.preferred_qualifications.map(
                (item: any, idx: number) => (
                  <li key={idx}>
                    {item.entity_type}: {item.entity_value}
                  </li>
                ),
              )}
            </ul>
          </div>
        )}

      {/* Resume-Mining Questions - Show for HR agent, hide for Skill Inference & Job Description Generator agents */}
      {agentId !== "skill_inference_agent" &&
        agentId !== "jd_composer_agent" &&
        agentId !== "modify_requirements" &&
        Array.isArray(mergedData.resume_mining_questions) &&
        mergedData.resume_mining_questions.length > 0 && (
          <div style={{ marginBottom: "7px" }}>
            <h3>First-interview screening questions</h3>
            <ul style={{ margin: "5px 0" }}>
              {mergedData.resume_mining_questions.map(
                (q: string, idx: number) => (
                  <li key={idx}>{q}</li>
                ),
              )}
            </ul>
          </div>
        )}

      {/* Scoring Weights (optional, future-safe) */}
      {mergedData.scoring_weights &&
        Object.keys(mergedData.scoring_weights).length > 0 && (
          <div style={{ marginBottom: "7px" }}>
            <h3>Scoring Weights</h3>

            <table
              style={{
                borderCollapse: "collapse",
                width: "100%",
                marginTop: "20px",
              }}
              className={styles.TableRow}
            >
              <thead>
                <tr>
                  <th style={{ border: "1px solid #ccc", padding: "8px" }}>
                    Category
                  </th>
                  <th style={{ border: "1px solid #ccc", padding: "8px" }}>
                    Weight
                  </th>
                </tr>
              </thead>

              <tbody>
                {Object.entries(mergedData.scoring_weights).map(
                  ([key, value], index) => (
                    <tr key={index}>
                      <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                        {snakeToTitleCase(key)}
                      </td>
                      <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                        {typeof value === "number" ? value : String(value)}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}

      {/* Final Question */}
      {question && <div className={styles.finalQuestion}>{question}</div>}
    </div>
  );
}
